/*
 eslint-disable react/jsx-one-expression-per-line, react/jsx-curly-newline, react/no-array-index-key
 */
import React, { useState, useEffect, useCallback } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

// Components
import { GroupingModal } from 'components';

// Types
import { ApplicationState } from 'store';
import { EntryGroupedInterface } from 'store/ducks/reports/types';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { getPaymentType } from 'helpers/getPaymentType';

// Contants
import CONST_QRCODE from 'constants/qrcodes';

// Styles
import useStyles from '../../chartReport.styles';

interface TableAgrupmentsChartInterface {
  entrysGroupedList?: EntryGroupedInterface[];
}

const TableAgrupmentsChart: React.FC<TableAgrupmentsChartInterface> = ({
  entrysGroupedList,
}) => {
  const classes = useStyles();

  const [grouping, setGrouping] = useState<any[]>([]);
  const [orderGrouping, setOrderGrouping] = useState<Order>('asc');
  const [orderByGrouping, setOrderByGrouping] = useState('usedAt');
  const entryReportListGrouped = useSelector((state: ApplicationState) => {
    if (entrysGroupedList?.length) return entrysGroupedList;
    return state.reports.entryReportListGrouped;
  });

  const selectedItems = useSelector(
    (state: ApplicationState) => state.groupingModal.selectedItems,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  useEffect(() => {
    if (entryReportListGrouped) {
      setGrouping(entryReportListGrouped);
    }
  }, [entryReportListGrouped]);

  const handleRequestSortGrouping = useCallback(
    (event: React.MouseEvent<unknown>, property: string) => {
      const isAsc = orderByGrouping === property && orderGrouping === 'asc';
      setOrderGrouping(isAsc ? 'desc' : 'asc');
      setOrderByGrouping(property);
    },
    [orderGrouping, orderByGrouping],
  );

  const formatQrcodeType = (qrcodeType: number) =>
    CONST_QRCODE.ENTRY.find((e) => e.KEY === qrcodeType)?.VALUE || '--';

  return (
    <TableContainer className={classes.table}>
      <Table>
        <TableHead>
          {grouping.length > 0 && (
            <TableRow>
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderByGrouping === 'position' ? orderGrouping : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={
                    orderByGrouping === 'position' ? orderGrouping : 'asc'
                  }
                  onClick={(event) =>
                    handleRequestSortGrouping(event, 'position')
                  }>
                  Posição
                  {orderByGrouping === 'position' ? (
                    <span className={classes.visuallyHidden}>
                      {orderGrouping === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              {selectedItems.find((e) => e.key === 'usedDate') && (
                <TableCell
                  variant='head'
                  align='left'
                  sortDirection={
                    orderByGrouping === 'usedDate' ? orderGrouping : false
                  }>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={
                      orderByGrouping === 'usedDate' ? orderGrouping : 'asc'
                    }
                    onClick={(event) =>
                      handleRequestSortGrouping(event, 'usedDate')
                    }>
                    Data
                    {orderByGrouping === 'usedDate' ? (
                      <span className={classes.visuallyHidden}>
                        {orderGrouping === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              )}
              {selectedItems.find((e) => e.key === 'dbdId') && (
                <TableCell
                  variant='head'
                  align='left'
                  sortDirection={
                    orderByGrouping === 'dbdId' ? orderGrouping : false
                  }>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={
                      orderByGrouping === 'dbdId' ? orderGrouping : 'asc'
                    }
                    onClick={(event) =>
                      handleRequestSortGrouping(event, 'dbdId')
                    }>
                    DBD
                    {orderByGrouping === 'dbdId' ? (
                      <span className={classes.visuallyHidden}>
                        {orderGrouping === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              )}
              {selectedItems.find((e) => e.key === 'paymentType') && (
                <TableCell
                  variant='head'
                  align='left'
                  sortDirection={
                    orderByGrouping === 'paymentType' ? orderGrouping : false
                  }>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={
                      orderByGrouping === 'paymentType' ? orderGrouping : 'asc'
                    }
                    onClick={(event) =>
                      handleRequestSortGrouping(event, 'paymentType')
                    }>
                    Forma de pagamento
                    {orderByGrouping === 'paymentType' ? (
                      <span className={classes.visuallyHidden}>
                        {orderGrouping === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              )}
              {selectedItems.find((e) => e.key === 'qrcodeType') && (
                <TableCell
                  variant='head'
                  align='left'
                  sortDirection={
                    orderByGrouping === 'qrcodeType' ? orderGrouping : false
                  }>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={
                      orderByGrouping === 'qrcodeType' ? orderGrouping : 'asc'
                    }
                    onClick={(event) =>
                      handleRequestSortGrouping(event, 'qrcodeType')
                    }>
                    Tipo de QRCode
                    {orderByGrouping === 'qrcodeType' ? (
                      <span className={classes.visuallyHidden}>
                        {orderGrouping === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderByGrouping === 'totalValue' ? orderGrouping : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={
                    orderByGrouping === 'totalValue' ? orderGrouping : 'asc'
                  }
                  onClick={(event) =>
                    handleRequestSortGrouping(event, 'totalValue')
                  }>
                  Valor
                  {orderByGrouping === 'totalValue' ? (
                    <span className={classes.visuallyHidden}>
                      {orderGrouping === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderByGrouping === 'count' ? orderGrouping : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={
                    orderByGrouping === 'count' ? orderGrouping : 'asc'
                  }
                  onClick={(event) =>
                    handleRequestSortGrouping(event, 'count')
                  }>
                  Quantidade
                  {orderByGrouping === 'count' ? (
                    <span className={classes.visuallyHidden}>
                      {orderGrouping === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              {transportOperatorSelected.type === 1 && (
                <TableCell
                  variant='head'
                  align='left'
                  sortDirection={
                    orderByGrouping === 'transportOperatorId'
                      ? orderGrouping
                      : false
                  }>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={
                      orderByGrouping === 'transportOperatorId'
                        ? orderGrouping
                        : 'asc'
                    }
                    onClick={(event) =>
                      handleRequestSortGrouping(event, 'transportOperatorId')
                    }>
                    Operação
                    {orderByGrouping === 'transportOperatorId' ? (
                      <span className={classes.visuallyHidden}>
                        {orderGrouping === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              )}{' '}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {!selectedItems.length && (
            <Grid
              container
              justifyContent='center'
              alignItems='center'
              direction='column'>
              <Typography className={classes.message}>
                Nenhum agrupamento... (
                <strong>Use o botão abaixo para agrupar</strong>)
              </Typography>

              <GroupingModal
                classGroup={classes.classGroup}
                groupOptions={[
                  { key: 'usedDate', value: 'Data' },
                  { key: 'dbdId', value: 'DBD' },
                  { key: 'paymentType', value: 'Forma de pagamento' },
                  { key: 'qrcodeType', value: 'Tipo de QRCode' },
                ]}
              />
            </Grid>
          )}

          {selectedItems.length > 0 &&
            stableSort(
              grouping,
              getComparator(orderGrouping, orderByGrouping),
            ).map((item: any) => (
              <TableRow key={`row-${item.position}`}>
                <TableCell>{item.position}</TableCell>
                {selectedItems.find((e) => e.key === 'usedDate') && (
                  <TableCell>{item.usedDate || ''}</TableCell>
                )}
                {selectedItems.find((e) => e.key === 'dbdId') && (
                  <TableCell>{item.dbdId}</TableCell>
                )}
                {selectedItems.find((e) => e.key === 'paymentType') && (
                  <TableCell>{getPaymentType(item.paymentType)}</TableCell>
                )}
                {selectedItems.find((e) => e.key === 'qrcodeType') && (
                  <TableCell>{formatQrcodeType(item.qrcodeType)}</TableCell>
                )}
                <TableCell>
                  {`R$ ${item.totalValue?.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })}`}
                </TableCell>
                <TableCell>{item.count}</TableCell>
                {transportOperatorSelected.type === 1 && (
                  <TableCell>
                    {transportOperatorsList.find(
                      (e) => e.id === item?.transportOperatorId,
                    )?.name || '---'}
                  </TableCell>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { TableAgrupmentsChart };
export default TableAgrupmentsChart;
